import type { Coordinates, LineCoordinates, PolygonCoordinates, RectCoordinates } from '@ricoh/ria.client.core/types/component/layout.type';

import { ClientError } from '@ricoh/ria.client.core/errors';

import type { OCRPositionAzureAI, OCRPositionBox, OCRPositionNatifAI, OCRPositionPolygon } from '~/common/model/api/batch';

import type { ExtractOCROptions, ExtractOCRResult, ExtractOCRResultAzureAI, ExtractOCRResultNatifAI } from './_extract.type';
import type { LassoMode, LassoPosition } from './_lasso.type';

import { isPosition, rotateOCRPosition } from './_position';

const OVERLAP_THRESHOLD = 0.5;

/**
 * Check point inside polygon. -> Ray Crossing Algorithm.
 *
 * @param {Coordinates} point Point Coordinates.
 * @param {PolygonCoordinates} polygon Polygon Coordinates.
 *
 * @returns {boolean} Is point inside polygon.
 */
export function isPointInPolygon({ x, y }: Coordinates, polygon: PolygonCoordinates): boolean {
  let inside = false;

  for (let i = 0, j = polygon.length - 2; i < polygon.length; j = i, i += 2) {
    const x1 = polygon[i]!;
    const y1 = polygon[i + 1]!;
    const x2 = polygon[j]!;
    const y2 = polygon[j + 1]!;

    const intersect = y1 > y !== y2 > y && x < ((x2 - x1) * (y - y1)) / (y2 - y1) + x1;
    if (intersect) {
      inside = !inside;
    }
  }

  return inside;
}

/**
 * CHeck if two line segments intersect.
 *
 * The function takes two line segments defined by their endpoints:
 *
 * - Segment 1: (p1, p2) with endpoints p1(x1, y1) and p2(x2, y2)
 * - Segment 2: (p3, p4) with endpoints p3(x3, y3) and p4(x4, y4)
 *
 * It uses the **2D cross product** to check if the segments intersect. The cross product determines the relative orientation of points.
 *
 * The segments are considered to intersect if:
 *
 * - The two segments "straddle" each other, i.e., each segment has the other segment on opposite sides.
 *
 * @param {LineCoordinates} line1 Line 1.
 * @param {LineCoordinates} line2 Line 2.
 *
 * @returns {boolean} Are two line segments intersect.
 */
function isSegmentsIntersect([p1, p2]: LineCoordinates, [p3, p4]: LineCoordinates): boolean {
  const crossProduct = ([{ x: x1, y: y1 }, { x: x2, y: y2 }]: LineCoordinates, { x: x3, y: y3 }: Coordinates) => (x1 - x3) * (y2 - y3) - (y1 - y3) * (x2 - x3);

  const d1 = crossProduct([p1, p2], p3);
  const d2 = crossProduct([p1, p2], p4);
  const d3 = crossProduct([p3, p4], p1);
  const d4 = crossProduct([p3, p4], p2);

  return d3 * d4 < 0 && d1 * d2 < 0;
}

/**
 * Check is rectangle intersecting with polygon.
 *
 * @param {RectCoordinates} rect Rect Coordinates.
 * @param {PolygonCoordinates} polygon Polygon Coordinates.
 *
 * @returns {boolean} Is rectangle intersecting with polygon.
 */
function isRectangleIntersectingPolygon(rect: RectCoordinates, polygon: PolygonCoordinates): boolean {
  // * Check rect point is inside polygon or not.
  if (
    isPointInPolygon({ x: rect.x1, y: rect.y1 }, polygon) ||
    isPointInPolygon({ x: rect.x2, y: rect.y1 }, polygon) ||
    isPointInPolygon({ x: rect.x2, y: rect.y2 }, polygon) ||
    isPointInPolygon({ x: rect.x1, y: rect.y2 }, polygon)
  ) {
    return true;
  }

  // * Check polygon point is inside rect or not.
  const rectPolygon: PolygonCoordinates = [rect.x1, rect.y1, rect.x2, rect.y1, rect.x2, rect.y2, rect.x1, rect.y2];
  const [x1, y1, x2, y2, x3, y3, x4, y4] = polygon;
  if (
    isPointInPolygon({ x: x1, y: y1 }, rectPolygon) ||
    isPointInPolygon({ x: x2, y: y2 }, rectPolygon) ||
    isPointInPolygon({ x: x3, y: y3 }, rectPolygon) ||
    isPointInPolygon({ x: x4, y: y4 }, rectPolygon)
  ) {
    return true;
  }

  // * Check rect edge segment is inside polygon edge segment or not.
  for (let i = 0; i < polygon.length; i += 2) {
    const polygonLine: LineCoordinates = [
      { x: polygon[i]!, y: polygon[i + 1]! },
      { x: polygon[(i + 2) % polygon.length]!, y: polygon[(i + 3) % polygon.length]! },
    ];

    const reactLine1: LineCoordinates = [
      { x: rect.x1, y: rect.y1 },
      { x: rect.x2, y: rect.y1 },
    ];
    const reactLine2: LineCoordinates = [
      { x: rect.x2, y: rect.y1 },
      { x: rect.x2, y: rect.y2 },
    ];
    const reactLine3: LineCoordinates = [
      { x: rect.x2, y: rect.y2 },
      { x: rect.x1, y: rect.y2 },
    ];
    const reactLine4: LineCoordinates = [
      { x: rect.x1, y: rect.y2 },
      { x: rect.x1, y: rect.y1 },
    ];

    if (
      isSegmentsIntersect(polygonLine, reactLine1) ||
      isSegmentsIntersect(polygonLine, reactLine2) ||
      isSegmentsIntersect(polygonLine, reactLine3) ||
      isSegmentsIntersect(polygonLine, reactLine4)
    ) {
      return true;
    }
  }

  return false;
}

/**
 * Sort OCR Position Boxes.
 *
 * @param {OCRPositionBox[]} boxes The OCR Position Boxes.
 * @param {number} overlapThreshold The overlap threshold.
 *
 * @returns {OCRPositionBox[]} The Sorted OCR Position Boxes.
 */
export function sortOCRPositionBoxes(boxes: OCRPositionBox[], overlapThreshold = OVERLAP_THRESHOLD): OCRPositionBox[] {
  if (boxes.length <= 1) {
    return boxes;
  }

  // * Ascending order by y1
  const sortedByY = boxes.toSorted((box1, box2) => box1.y1 - box2.y1);

  // * Group boxes into rows based on the Y-axis intersection.
  // * Compare with the box currently having the largest y1 in the row to avoid cumulative bias.
  const rows: OCRPositionBox[][] = [];
  let currentRow: OCRPositionBox[] = [];
  sortedByY.forEach((box) => {
    // * For new line, just push and continue
    if (!currentRow.length) {
      currentRow.push(box);
      return;
    }

    const lastBoxInRow = currentRow[currentRow.length - 1]!;
    const prevY1 = lastBoxInRow.y1;
    const prevY2 = lastBoxInRow.y2;
    const currY1 = box.y1;
    const currY2 = box.y2;
    const prevHeight = prevY2 - prevY1;
    const currHeight = currY2 - currY1;

    // * Find overlap Y-axis intersection.
    const overlapY1 = Math.max(prevY1, currY1);
    const overlapY2 = Math.min(prevY2, currY2);
    const overlapHeight = Math.max(0, overlapY2 - overlapY1);

    // * Calculate overlap ratio
    const minHeight = Math.min(prevHeight, currHeight);
    const overlapRatio = minHeight > 0 ? overlapHeight / minHeight : 0;

    // * The overlap ratio exceeds the threshold -> Same line of text
    if (overlapRatio > overlapThreshold) {
      currentRow.push(box);
    }
    // * Next line
    else {
      rows.push(currentRow);
      currentRow = [box];
    }
  });
  // * Push remain row
  if (currentRow.length > 0) {
    rows.push(currentRow);
  }

  // * Ascending order by x1 for each line
  const result = rows.map((row) => row.toSorted((box1, box2) => box1.x1 - box2.x1));

  return result.flat();
}

const extract = {
  natifAI: (pageOCRPosition: OCRPositionNatifAI, { page, selectorPosition }: LassoPosition): ExtractOCRResultNatifAI => {
    const result: ExtractOCRResultNatifAI = {
      position: {
        pageId: selectorPosition.pageId,
        pageWidth: pageOCRPosition.pageWidth,
        pageHeight: pageOCRPosition.pageHeight,
        boxes: [],
      },
    };

    const selectorBox = selectorPosition.box;
    if (!selectorBox) {
      return result;
    }

    const rotatedPageOCRPosition = rotateOCRPosition(page, pageOCRPosition);

    // * Mapping selector on page size
    const widthRatio = rotatedPageOCRPosition.pageWidth / selectorPosition.pageWidth;
    const heightRatio = rotatedPageOCRPosition.pageHeight / selectorPosition.pageHeight;
    const selector: RectCoordinates = {
      x1: selectorBox.x1 * widthRatio,
      x2: selectorBox.x2 * widthRatio,
      y1: selectorBox.y1 * heightRatio,
      y2: selectorBox.y2 * heightRatio,
    };

    // * Logic: Box is selected if 2 boxes overlap each other
    // *   - Overlap on x axis
    // *   - Overlap on y axis
    const selectedBoxes = rotatedPageOCRPosition.boxes.reduce<OCRPositionBox[]>((prev, { x1, x2, y1, y2 }, index) => {
      if (
        // * Overlap on x axis
        x1 <= selector.x2 &&
        x2 >= selector.x1 &&
        // * Overlap on y axis
        y1 <= selector.y2 &&
        y2 >= selector.y1
      ) {
        const box = pageOCRPosition.boxes[index];
        if (box) {
          prev.push(box);
        }
      }

      return prev;
    }, []);

    // * Sort boxes
    result.position.boxes = sortOCRPositionBoxes(selectedBoxes);

    // * Extract text
    result.text = result.position.boxes
      .reduce<string[]>((prev, { text }) => {
        if (text) {
          prev.push(text);
        }

        return prev;
      }, [])
      .join(' ');

    return result;
  },

  azureAI: (pageOCRPosition: OCRPositionAzureAI, lassoMode: LassoMode, { page, selectorPosition }: LassoPosition): ExtractOCRResultAzureAI => {
    const result: ExtractOCRResultAzureAI = {
      position: {
        pageId: selectorPosition.pageId,
        pageWidth: pageOCRPosition.pageWidth,
        pageHeight: pageOCRPosition.pageHeight,
        regions: [],
      },
    };

    const selectorBox = selectorPosition.box;
    if (!selectorBox) {
      return result;
    }

    const rotatedPageOCRPosition = rotateOCRPosition(page, pageOCRPosition);
    const prop: keyof OCRPositionAzureAI = lassoMode === 'single' ? 'regions' : 'paragraphs';
    const joinSeparator = lassoMode === 'single' ? ' ' : '\n';

    // * Mapping selector on page size
    const widthRatio = rotatedPageOCRPosition.pageWidth / selectorPosition.pageWidth;
    const heightRatio = rotatedPageOCRPosition.pageHeight / selectorPosition.pageHeight;
    const selector: RectCoordinates = {
      x1: selectorBox.x1 * widthRatio,
      x2: selectorBox.x2 * widthRatio,
      y1: selectorBox.y1 * heightRatio,
      y2: selectorBox.y2 * heightRatio,
    };

    const selectedRegions = rotatedPageOCRPosition[prop]?.reduce<OCRPositionPolygon[]>((prev, { polygon }, index) => {
      if (isRectangleIntersectingPolygon(selector, polygon)) {
        const region = pageOCRPosition[prop]?.[index];
        if (region) {
          prev.push(region);
        }
      }

      return prev;
    }, []);

    // * Sort boxes ------------------
    // Need to sort boxes because the order of boxes in pageOCRPosition is not sorted.
    // * -----------------------------------------------------------

    result.position.regions = selectedRegions ?? [];

    // * Extract text
    result.text = result.position.regions
      .reduce<string[]>((prev, { content }) => {
        if (content) {
          prev.push(content);
        }

        return prev;
      }, [])
      .join(joinSeparator);

    return result;
  },
};

/**
 * Extract data from page OCR position.
 *
 * @param {ExtractOCROptions} options Extract OCR Options.
 *
 * @returns {ExtractOCRResult} OCR Result.
 */
export function extractOCR({ pageOCRPosition, lassoMode, lassoPosition }: ExtractOCROptions): ExtractOCRResult {
  if (isPosition(pageOCRPosition, 'natifAI')) {
    return extract.natifAI(pageOCRPosition, lassoPosition);
  }

  if (isPosition(pageOCRPosition, 'azureAI')) {
    return extract.azureAI(pageOCRPosition, lassoMode, lassoPosition);
  }

  throw new ClientError({
    module: 'logic',
    message: 'Invalid OCR position',
    cause: {
      pageOCRPosition,
      lassoPosition,
    },
  });
}
